import { faPhone, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useAsyncGuiHandler from '../../hooks/useAsyncHandler';
import { useMeeting } from '../../hooks/useMeeting';
import type { Meetingroom } from '../../interfaces/interfaces';
import { useSelector } from '../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../services/helper';
import { fileUpload, updateMeeting } from '../../services/services';
import VyAvatar from '../common/VyAvatar';
import VyButton from '../common/VyButton';
import VyTextArea from '../common/VyTextArea';
import VyTextField from '../common/VyTextField';
import { VyUploadCrop } from '../common/VyUploadCrop';

interface MeetingRoomProps {
  room: Meetingroom;
  secondaryFieldName?: string;
  additionalInfos?: string;
  withoutJoinFunction?: boolean;
  allowUpdates?: boolean;
  refresh?: () => void;
}

/**
 * a component which renders a view for the account panel of a single meetingroom
 * @param room the room to render
 * @param withoutJoinFunction wether to display the join button
 * @param secondaryFieldName the name of the secondary field beeing displayed. by default contactposition is used
 * @param updates wether it is an editable field
 * @param refresh
 * @constructor
 */
const MeetingRoom = ({ room, withoutJoinFunction, secondaryFieldName, additionalInfos, allowUpdates, refresh }: MeetingRoomProps) => {
  const secondaryFieldNameFinal: string = secondaryFieldName || 'contactposition';
  const { t } = useTranslation();
  const { control } = useForm();
  const [fieldsLoading, setFieldsLoading] = useState<Array<string>>([]);
  const [handleAsync] = useAsyncGuiHandler();
  const meeting = useMeeting();

  const [image, setImage] = useState(room.image);

  const event = useSelector((state) => state.admin.event);

  const uploadImage = async (b: Blob) => {
    const data = await fileUpload(b);
    await updateMeeting({ meetingroomid: room.id, image: data.data.fileId });
    setImage(data.data.fileId);
  };

  async function focusOut({ target: { name, value } }: React.FocusEvent<{ value: string; name: string }>) {
    setFieldsLoading([...fieldsLoading, name]);
    await updateMeeting({ meetingroomid: room.id, [name]: value });
    setFieldsLoading(fieldsLoading.filter((e) => e !== name));
    if (refresh) refresh();
  }

  return (
    <>
      <form className="shadow bg-gray-50 p-2.5 rounded m-2 w-full">
        <div className="flex flex-col justify-center items-center md xl:flex-row space-y-1 space-x-1">
          {event?.template?.uiTemplate !== 'vystem-zukunftsrat' && event?.template?.uiTemplate !== 'vbw-additive-manufacturing' && (
            <div className="flex justify-center mr-2">
              {allowUpdates !== false && (
                <VyUploadCrop cropShape="round" onImage={uploadImage}>
                  <div className="flex justify-center items-center relative cursor-pointer">
                    <div className="bg-black bg-opacity-50 z-30 absolute rounded-full p-2">
                      <FontAwesomeIcon className="text-white z-30 " style={{ height: '15px', width: '20px', cursor: 'pointer' }} icon={faUpload} />
                    </div>
                    <VyAvatar
                      alt="logo"
                      src={createDisplayLink(image, undefined, undefined, undefined, undefined, undefined, true)}
                      style={{ width: '64px', height: '64px' }}
                    />
                  </div>
                </VyUploadCrop>
              )}

              {allowUpdates === false && (
                <div className="flex justify-center">
                  <Avatar
                    alt="logo"
                    src={createDisplayLink(image, undefined, undefined, undefined, undefined, undefined, true)}
                    style={{ width: '64px', height: '64px' }}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex w-full flex-col space-y-1">
            {room.meetingtype === 'networking' && (
              <>
                <label className="w-full">
                  {allowUpdates !== false && (
                    <Controller
                      control={control}
                      name="name"
                      defaultValue={room.name}
                      render={({ field }) => (
                        <VyTextField
                          {...field}
                          fullWidth
                          onBlur={(e) => room.name !== e.target.value && handleAsync(focusOut(e), undefined, false)}
                          className="rounded focus:outline-0 text-sm pb-1 p-1 pr-10"
                          inputProps={{ maxLength: 80 }}
                          label={t('booth:name-consultant')}
                          loading={fieldsLoading.includes('name')}
                        />
                      )}
                    />
                  )}
                  {allowUpdates === false && room.name}
                </label>
                <label className="w-full">
                  {allowUpdates !== false && (
                    <Controller
                      control={control}
                      name="subtext"
                      defaultValue={room.subtext}
                      render={({ field }) => (
                        <VyTextField
                          {...field}
                          fullWidth
                          onBlur={(e) => room.subtext !== e.target.value && handleAsync(focusOut(e), undefined, false)}
                          className="rounded focus:outline-0 text-sm pb-1 p-1 pr-10"
                          inputProps={{ maxLength: 80 }}
                          label={room.subtitle}
                          loading={fieldsLoading.includes('subtext')}
                        />
                      )}
                    />
                  )}
                  {allowUpdates === false && room.subtext}
                </label>
              </>
            )}

            {room.meetingtype !== 'networking' && (
              <label className="w-full">
                {allowUpdates !== false && (
                  <Controller
                    control={control}
                    name="contactperson"
                    defaultValue={room.contactperson}
                    render={({ field }) => (
                      <VyTextField
                        {...field}
                        fullWidth
                        onBlur={(e) => room.contactperson !== e.target.value && handleAsync(focusOut(e), undefined, false)}
                        className="rounded focus:outline-0 text-sm pb-1 p-1 pr-10"
                        inputProps={{ maxLength: 80 }}
                        label={t('booth:name-consultant')}
                        loading={fieldsLoading.includes('contactperson')}
                      />
                    )}
                  />
                )}
                {allowUpdates === false && room.contactperson}
              </label>
            )}

            {room.meetingtype !== 'networking' && (
              <label className="w-full mt-1">
                {allowUpdates !== false && (
                  <Controller
                    control={control}
                    name={secondaryFieldNameFinal}
                    defaultValue={room[secondaryFieldNameFinal]}
                    render={({ field }) => (
                      <VyTextField
                        {...field}
                        fullWidth
                        onBlur={(e) => room[secondaryFieldNameFinal] !== e.target.value && handleAsync(focusOut(e), undefined, false)}
                        className="rounded focus:outline-0 text-sm pb-1 p-1 pr-10"
                        inputProps={{ maxLength: 80 }}
                        label={secondaryFieldNameFinal === 'contactposition' ? t('booth:position-consultant') : ''}
                        loading={fieldsLoading.includes(secondaryFieldNameFinal)}
                      />
                    )}
                  />
                )}
                {allowUpdates === false && room[secondaryFieldNameFinal]}
              </label>
            )}
          </div>

          <div className="flex items-center w-full md:w-auto">
            {event?.template.uiTemplate !== 'vystem-light' ||
              (room?.meetingtype === 'networking' && (
                <label className="description w-full ">
                  {allowUpdates !== false && (
                    <VyTextArea
                      name="description"
                      onBlur={(e) => room.description !== e.target.value && handleAsync(focusOut(e), undefined, false)}
                      placeholder={t('booth:your-text')}
                      className="rounded text-[10px] focus:outline-0  p-1"
                      maxLength={250}
                      defaultValue={room.description}
                      fullWidth
                      loading={fieldsLoading.includes('description')}
                      rows={3}
                    />
                  )}
                  {allowUpdates === false && room.description}
                </label>
              ))}
            <span>{additionalInfos}</span>
          </div>

          <div>
            {!withoutJoinFunction && (
              <VyButton onClick={() => meeting.redirectToRoom(room)} tabIndex={0} compact className="w-full py-6 px-2 lg:mt-2 xl:mt-0">
                <FontAwesomeIcon className="sm:mr-1" icon={faPhone} />
                {t('common:join')}
              </VyButton>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default MeetingRoom;
